import { useEffect } from "react";

import { Grid, Stack, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import _ from "lodash";

import LinearStepper from "../../../common/components/LinearStepper";
import Success from "../../shared/SuccessModal";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import DropzoneExtended from "../../../common/components/mui-extended/DropzoneExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { Mode, Step, useInputForm } from "../../../common/hooks/useInputForm";
import useUserContext from "../../../hooks/useUserContext";
import certificationService from "../../../services/certificationService";
import fileService from "../../../common/services/fileService";
import { CertificationInputDto, CertificationProps, CertificationTZMode } from "../../../interfaces/Certification";
import { RegisterType } from "../../../common/interfaces/enums/RegisterType";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { getRoutePath } from "./utils";
import { useNavigate } from "react-router-dom";
import { PublicRoutesConfig } from "../../RoutesConfig";

export default function Input({ registerType }: CertificationProps) {
  const navigate = useNavigate();
  const loading = useAxiosLoader(false);

  const { userId, isOwner } = useUserContext();
  const routePath = getRoutePath(registerType);

  const defaultModel: CertificationInputDto = {
    userId,
    registerType: registerType,
  };

  const form = useInputForm<CertificationInputDto>({
    defaultModel: defaultModel,
    addAsync: certificationService.addCertificationAsync,
    updateAsync: certificationService.updateCertificationAsync,
    validate: async (model: CertificationInputDto) => {
      var isAtLeastOneFilled = false;
      var isRegistrationNumberSignatureFilled = model.registrationNumberSignature;
      var isIssueDateSignatureFilled = model.issueDateSignature;
      var isRegistrationNumberContentFilled = model.registrationNumberContent;
      var isIssueDateContentFilled = model.issueDateContent;

      if (
        registerType === RegisterType.TZCertifications &&
        !isRegistrationNumberSignatureFilled &&
        isIssueDateSignatureFilled
      ) {
        form.setError(nameof(defaultModel.registrationNumberSignature), ErrorMessages.Required);
        isAtLeastOneFilled = true;
      } else if (
        registerType === RegisterType.TZCertifications &&
        isRegistrationNumberSignatureFilled &&
        !isIssueDateSignatureFilled
      ) {
        form.setError(nameof(defaultModel.issueDateSignature), ErrorMessages.Required);
        isAtLeastOneFilled = true;
      } else if (isRegistrationNumberSignatureFilled && isIssueDateSignatureFilled) {
        await certificationService
          .getCertificationIdByYearAsync(
            registerType,
            userId,
            model.registrationNumberSignature,
            model.issueDateSignature
          )
          .then(({ data: certificationId }) => {
            if (certificationId && certificationId !== form.id) {
              form.setError(nameof(defaultModel.registrationNumberSignature), ErrorMessages.AlreadyExists);
            }
          });

        isAtLeastOneFilled = true;
      }

      if (!isRegistrationNumberContentFilled && isIssueDateContentFilled) {
        form.setError(nameof(defaultModel.registrationNumberContent), ErrorMessages.Required);
        isAtLeastOneFilled = true;
      } else if (isRegistrationNumberContentFilled && !isIssueDateContentFilled) {
        form.setError(nameof(defaultModel.issueDateContent), ErrorMessages.Required);
        isAtLeastOneFilled = true;
      } else if (isRegistrationNumberContentFilled && isIssueDateContentFilled) {
        await certificationService
          .getCertificationIdByYearAsync(registerType, userId, model.registrationNumberContent, model.issueDateContent)
          .then(({ data: certificationId }) => {
            if (certificationId && certificationId !== form.id) {
              form.setError(nameof(defaultModel.registrationNumberContent), ErrorMessages.AlreadyExists);
            }
          });

        isAtLeastOneFilled = true;
      }

      if (
        isRegistrationNumberSignatureFilled &&
        isIssueDateSignatureFilled &&
        isRegistrationNumberContentFilled &&
        isIssueDateContentFilled &&
        model.registrationNumberSignature === model.registrationNumberContent &&
        model.issueDateSignature?.getFullYear() === model.issueDateContent?.getFullYear()
      ) {
        form.setError(nameof(defaultModel.registrationNumberContent), ErrorMessages.UniquePairRequired);
      }

      if (registerType === RegisterType.TZCertifications && !isAtLeastOneFilled) {
        form.setError(nameof(defaultModel.registrationNumberSignature), ErrorMessages.AtLeastOneFilled);
      }
    },
  });

  useEffect(() => {
    (async () => {
      if (form.mode === Mode.EDIT) {
        await certificationService.getCertificationAsync(form.id).then(({ data: certification }) => {
          const model: CertificationInputDto = {
            ...certification,
            userId: userId,
            certificationId: form.id,
            fileId: certification.file?.fileId,
          };

          if (isOwner(certification.ownerUserIds)) {
            form.recreateForm(model);
          } else {
            navigate(PublicRoutesConfig.Home.Path);
          }
        });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderCertificationData = () => {
    const getCertificationTZMode = () => {
      const hasAuthenticationSignature =
        form.getValues(nameof(defaultModel.registrationNumberSignature)) &&
        form.getValues(nameof(defaultModel.issueDateSignature));
      const hasAuthenticationContent =
        form.getValues(nameof(defaultModel.registrationNumberContent)) &&
        form.getValues(nameof(defaultModel.issueDateContent));

      if (hasAuthenticationSignature && hasAuthenticationContent) {
        return CertificationTZMode.Both;
      } else if (hasAuthenticationSignature) {
        return CertificationTZMode.Signature;
      } else if (hasAuthenticationContent) {
        return CertificationTZMode.Content;
      }
    };

    switch (registerType) {
      case RegisterType.TZCertifications:
        const certificationTZMode = getCertificationTZMode();

        switch (certificationTZMode) {
          case CertificationTZMode.Signature:
            return (
              <>
                {`на документ ${form.getValues(nameof(defaultModel.registrationNumberSignature))} / ${dateToStringShort(
                  form.getValues(nameof(defaultModel.issueDateSignature))
                )} за удостоверяване по ТЗ`}
              </>
            );
          case CertificationTZMode.Content:
            return (
              <>
                {`на документ ${form.getValues(nameof(defaultModel.registrationNumberContent))} / ${dateToStringShort(
                  form.getValues(nameof(defaultModel.issueDateContent))
                )} за удостоверяване по ТЗ`}
              </>
            );
          case CertificationTZMode.Both:
            return (
              <>
                {`на документ ${form.getValues(nameof(defaultModel.registrationNumberSignature))} / ${dateToStringShort(
                  form.getValues(nameof(defaultModel.issueDateSignature))
                )} и ${form.getValues(nameof(defaultModel.registrationNumberContent))} / ${dateToStringShort(
                  form.getValues(nameof(defaultModel.issueDateContent))
                )} за удостоверяване по ТЗ`}
              </>
            );
          default:
            break;
        }
        break;
      case RegisterType.ZOZCertifications:
        return (
          <>
            {`на документ ${form.getValues(nameof(defaultModel.registrationNumberSignature))} / ${dateToStringShort(
              form.getValues(nameof(defaultModel.issueDateSignature))
            )} за удостоверяване по ЗОЗ`}
          </>
        );
      default:
        break;
    }
  };

  const renderSuccess = () => <Success routePath={routePath}>{renderCertificationData()}</Success>;

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended
          title={
            defaultModel.registerType === RegisterType.TZCertifications
              ? "Удостоверяване на подпис"
              : "Данни за удостоверяване"
          }
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Регистрационен номер"
                name={nameof(defaultModel.registrationNumberSignature)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={
                  registerType === RegisterType.TZCertifications ? Rules.Number : { ...Rules.Required, ...Rules.Number }
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата"
                name={nameof(defaultModel.issueDateSignature)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={
                  registerType === RegisterType.TZCertifications ? Rules.Date : { ...Rules.Required, ...Rules.Date }
                }
              />
            </Grid>
          </Grid>
        </SectionExtended>
        {defaultModel.registerType === RegisterType.TZCertifications && (
          <SectionExtended title="Удостоверяване на съдържание">
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextFieldExtended
                  label="Регистрационен номер"
                  name={nameof(defaultModel.registrationNumberContent)}
                  form={form}
                  disabled={form.step === Step.PREVIEW}
                  rules={Rules.Number}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePickerExtended
                  label="Дата"
                  name={nameof(defaultModel.issueDateContent)}
                  form={form}
                  disabled={form.step === Step.PREVIEW}
                  rules={Rules.Date}
                />
              </Grid>
            </Grid>
          </SectionExtended>
        )}
        <SectionExtended title="Участници">
          <TextFieldExtended
            name={nameof(defaultModel.participants)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended title="Извлечение">
          <TextFieldExtended
            name={nameof(defaultModel.statement)}
            multiline
            form={form}
            disabled={form.step === Step.PREVIEW}
            rules={Rules.Required}
          />
        </SectionExtended>
        <SectionExtended
          title="Сканирано копие (незадължително)"
          defaultExpanded={!!form.watch(nameof(defaultModel.fileId))}
        >
          <DropzoneExtended
            name={nameof(defaultModel.fileId)}
            form={form}
            disabled={form.step === Step.PREVIEW}
            initRequest={fileService.getFileMetadataById}
            uploadRequest={fileService.upload}
          />
        </SectionExtended>
        {form.mode === Mode.EDIT && (
          <SectionExtended title="Забележка (причини за извършване на корекцията)">
            <TextFieldExtended
              name={nameof(defaultModel.correctionReason)}
              multiline
              form={form}
              disabled={form.step === Step.PREVIEW}
              rules={Rules.Required}
            />
          </SectionExtended>
        )}
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3} minHeight={550}>
      <Typography variant="h5">Регистър {_.lowerFirst(routePath.Root.Label)}</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
